/* eslint-disable @typescript-eslint/dot-notation */
import { FeatureFlagKey, getWidgetData } from '@/services/widget-data';
import { WidgetApi } from '../api-definitions';
import getConfiguration, { EnvironmentConfiguration, getConfigurationForEnvironmentName } from '../shared/configuration';
import logger from '../shared/logger';
import baseModalScss from '@/widget/assets/styles/base-modal.scss';
import payInNModalScss from '@/widget/assets/styles/pay-in-n-modal.scss';
import { _createModal } from '@/widget/quadpay-modal';
import Modal from '@/widget/assets/components/BaseModal.vue';
import PayInNModal from '@/widget/assets/components/PayInNModal.vue';
import { BankPartner } from '@/services/widget-data/models';
import { BareModalOptions } from './widget-models';

let environmentConfiguration: EnvironmentConfiguration = getConfiguration();
logger.info(`Successfully instantiated the widget API in environment ${environmentConfiguration.environmentName}`);

/**
 * Displays the modal
 * @param widgetId The id to identify the modal to display
 */
function displayModal(widgetId) {
  const modalContainer = document
    .querySelector(`#qp-modal-${widgetId}`)
    .shadowRoot.getElementById('qp-modal__overlay');
  modalContainer.style.display = 'block';
  modalContainer.focus();
}

/**
 * Hides the modal
 */
function hideModal(widgetId): void {
  const modalContainer = document
    .querySelector(`#qp-modal-${widgetId}`)
    .shadowRoot.getElementById('qp-modal__overlay');
  const configuration = getConfiguration();

  // If our modal is open and we close it in any way, we should return focus to the link.
  if (modalContainer.style.display === 'block' && modalContainer.getAttribute('data-no-hide') !== 'true') {
    modalContainer.style.display = 'none';

    const widget = document.querySelector(configuration.tagname);
    if (widget) {
      const button: HTMLElement = widget.shadowRoot.querySelector('.qp-open-modal-button');
      button.focus();
    }
  }
}

/**
 * Displays a bare modal in the top-left of the DOM.
 * @param merchantId the merchant id to use when populating the modal's contents
 * @param amount the decimal amount of money for the purchase
 */
async function displayBareModal(options?: BareModalOptions): Promise<void> {
  if (options?.merchantId) {
    // set attributes based on query parameters
    const guidMatchRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    const guidIsValid = options?.merchantId?.match(guidMatchRegex);

    if (!guidIsValid) { // don't do anything without a valid merchant id
      console.error('`merchantId` must be a valid GUID.');
      return;
    }
  }

  if (options?.amount && typeof options?.amount !== 'number') { // don't do anything without a valid amount field
    console.error('`amount` must be a valid number.');
    return;
  }

  const merchantId = options?.merchantId ?? '00000000-0000-0000-0000-000000000000';
  const amount = options?.amount ?? 0.00;

  // Load the widget data for the merchant
  const widgetData = getWidgetData();
  widgetData.setMerchantId(merchantId);
  widgetData.setAmount(amount.toString());
  await widgetData.load();

  // read widgetData to better understand the modal to display
  const paymentSchedules = await widgetData.fetchPaymentSchedules(amount.toString());
  const isMerchantPayInZ = !!paymentSchedules?.length;

  const isPayIn8FeatureEnabled = widgetData.isFeatureEnabled(
    FeatureFlagKey.PayIn8Widget,
    false,
  );

  const usePayInZModal = isMerchantPayInZ && isPayIn8FeatureEnabled;

  // insert the modal into the DOM
  const baseModalComponent = usePayInZModal
    ? await _createModal('payInN', PayInNModal, payInNModalScss, merchantId)
    : await _createModal('base', Modal, baseModalScss, merchantId);

  // set configurations for the widget
  baseModalComponent['widgetId'] = merchantId;
  baseModalComponent['translatorLanguage'] = 'en-US';

  // determine if this modal should show extra disclosures
  const isFeeMerchant = widgetData.getMerchantFeeTiers().length > 0;
  const isWebBankMerchant = widgetData.getMerchantBankPartner() === BankPartner.WebBank;
  baseModalComponent['shouldShowWebbankFeeDisclosure'] = (isFeeMerchant && isWebBankMerchant) || options?.forceWebbankFeeDisclosures;
  baseModalComponent['shouldShowWebbankDisclosure'] = isWebBankMerchant || options?.forceWebbankDisclosures;

  // remove some styles so it doesn't look like the modal is hovering
  if (usePayInZModal) {
    const modalContainer = document
      .querySelector(`#qp-modal-${merchantId}`)
      .shadowRoot.getElementById('qp-modal__container');

    modalContainer.style.justifyContent = 'start';
    modalContainer.style.alignItems = 'start';
    modalContainer.style.background = 'transparent';

    // prevent the unexpected hiding of this particular modal (by clicking off of it or using the close button)
    const modalOverlay = document
      .querySelector(`#qp-modal-${merchantId}`)
      .shadowRoot.getElementById('qp-modal__overlay');
    modalOverlay.setAttribute('data-no-hide', 'true');

    const modalContent = document
      .querySelector(`#qp-modal-${merchantId}`)
      .shadowRoot.getElementById('qp-modal__content');

    modalContent.style.boxShadow = '0px 0px 0px black';

    // remove the close button
    const closeButton = modalContainer.querySelector<HTMLElement>('#qp-modal__close');
    if (closeButton != null) {
      closeButton.remove();
    }

  // PI4 modal
  } else {
    const modalContainer = document
      .querySelector(`#qp-modal-${merchantId}`)
      .shadowRoot.getElementById('qp-modal__container');

    // remove margin and box shadow
    modalContainer.style.margin = '0px';
    modalContainer.style.boxShadow = '0px 0px 0px black';
    modalContainer.style.display = 'block';
    modalContainer.parentElement.style.background = 'transparent';

    // remove the close button
    const closeButton = modalContainer.querySelector<HTMLElement>('#qp-modal__close');
    if (closeButton != null) {
      closeButton.remove();
    }
  }

  // finally, display the modal
  widgetApi.displayModal(merchantId);
}

/**
 * Updates the environment environmentConfigurationuration of widget to use the environment specified
 * @param environmentName The name of the environment to integrate with (e.g. Production, Sandbox, etc.)
 */
function updateEnvironment(environmentName: string): void {
  const currentEnvironment = environmentConfiguration;
  const loadedEnvironment = getConfigurationForEnvironmentName(environmentName);

  if (currentEnvironment?.environmentName !== loadedEnvironment?.environmentName) {
    environmentConfiguration = loadedEnvironment;
  }
}

/**
 * Retrieve widget ids
 */
function getWidgetIds() {
  const tagName = getConfiguration().tagname;
  const widgets = document.querySelectorAll(tagName);
  const widgetIds = [];
  widgets.forEach((widget) =>
    widgetIds.push(
      widget.getAttribute('widgetid') || widget.getAttribute('widgetId'),
    ),
  );
  return widgetIds;
}

function getWidgetVerbiage(attribute, isServiceFeeMerchant, isMFPPMerchant, hasFees): string {
  const WidgetVerbiageMap = Object.freeze({
    '4-easy-payments': '4 easy payments',
    '4-payments': '4 payments',
    '4-installments': 'Pay in 4 installments',
  });
  let verbiageKey: string = '4-payments';
  if (attribute === '4-installments' || (attribute === '4-easy-payments' && isMFPPMerchant)) {
    verbiageKey = attribute;
  } else if (isServiceFeeMerchant || isMFPPMerchant) {
    verbiageKey = '4-payments';
  } else if (WidgetVerbiageMap[attribute]) {
    verbiageKey = attribute;
  }
  verbiageKey = 'widget.' + verbiageKey;

  return verbiageKey;
}

const widgetApi: WidgetApi = {
  displayModal,
  hideModal,
  displayBareModal,
  updateEnvironment,
  getWidgetIds,
  getWidgetVerbiage,
};
export default widgetApi;

export const testables = {};
